import { c as _c } from "react/compiler-runtime";
import { createContext, useContext, type ReactNode, useState, useCallback, useSyncExternalStore } from 'react';
import { Toast as ToastComponent } from '@/app/_shared/components/Toast';
import { useLocalStorage } from '@/app/_shared/hooks/useLocalStorage';
import { ToastType } from '@/app/_shared/enums';
export interface Toast {
  id: number;
  message: string;
  type: ToastType;
  duration?: number;
}
type ToastArgs = {
  message: string;
  type?: ToastType;
  duration?: number;
};
const noSubscribe = () => () => {};
export const ToastContext = createContext<{
  /** Queue a toast, redirect friendly */
  queueToast: (args: ToastArgs) => void;
  /** Push a toast for show, clearing the active one */
  pushToast: (args: ToastArgs) => void;
  clearToasts: () => void;
} | undefined>(undefined);
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
export function ToastProvider(t0) {
  const $ = _c(27);
  const {
    children
  } = t0;
  const isLocalStorageAvailable = useSyncExternalStore(noSubscribe, _temp, _temp2);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    setValue: setQueuedToasts,
    getCurrentValue: getQueuedToasts,
    clearCurrentValue: clearToasts
  } = useLocalStorage("queuedToasts", t1);
  const [activeToast, setActiveToast] = useState();
  let t2;
  if ($[1] !== getQueuedToasts || $[2] !== isLocalStorageAvailable || $[3] !== setQueuedToasts) {
    t2 = args => {
      if (!isLocalStorageAvailable) {
        return;
      }
      const {
        message,
        type: t3,
        duration: t4
      } = args;
      const type = t3 === undefined ? ToastType.INFO : t3;
      const duration = t4 === undefined ? 3000 : t4;
      const id = Date.now();
      const newToast = {
        id,
        message,
        type,
        duration
      };
      setQueuedToasts([...getQueuedToasts(), newToast]);
    };
    $[1] = getQueuedToasts;
    $[2] = isLocalStorageAvailable;
    $[3] = setQueuedToasts;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const queueToast = t2;
  let t3;
  if ($[5] !== getQueuedToasts || $[6] !== isLocalStorageAvailable || $[7] !== setQueuedToasts) {
    t3 = args_0 => {
      if (!isLocalStorageAvailable) {
        return;
      }
      const {
        message: message_0,
        type: t4,
        duration: t5
      } = args_0;
      const type_0 = t4 === undefined ? ToastType.INFO : t4;
      const duration_0 = t5 === undefined ? 3000 : t5;
      const id_0 = Date.now();
      const newToast_0 = {
        id: id_0,
        message: message_0,
        type: type_0,
        duration: duration_0
      };
      const toasts = getQueuedToasts();
      toasts.shift();
      setQueuedToasts([newToast_0, ...toasts]);
      setActiveToast(newToast_0);
    };
    $[5] = getQueuedToasts;
    $[6] = isLocalStorageAvailable;
    $[7] = setQueuedToasts;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const pushToast = t3;
  let t4;
  if ($[9] !== getQueuedToasts || $[10] !== isLocalStorageAvailable || $[11] !== setQueuedToasts) {
    t4 = () => {
      if (!isLocalStorageAvailable) {
        return;
      }
      const remainingToasts = getQueuedToasts();
      remainingToasts.shift();
      setActiveToast(undefined);
      setQueuedToasts(remainingToasts);
    };
    $[9] = getQueuedToasts;
    $[10] = isLocalStorageAvailable;
    $[11] = setQueuedToasts;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const dequeueFirstToast = t4;
  if (isLocalStorageAvailable) {
    const queuedToasts = getQueuedToasts();
    if (queuedToasts && queuedToasts.length && !activeToast) {
      setActiveToast(queuedToasts.at(0));
    }
  }
  let t5;
  if ($[13] !== clearToasts || $[14] !== pushToast || $[15] !== queueToast) {
    t5 = {
      queueToast,
      pushToast,
      clearToasts
    };
    $[13] = clearToasts;
    $[14] = pushToast;
    $[15] = queueToast;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== activeToast || $[18] !== dequeueFirstToast || $[19] !== isLocalStorageAvailable) {
    t6 = isLocalStorageAvailable && !!activeToast && <ToastComponent duration={activeToast.duration} onClose={dequeueFirstToast} message={activeToast.message} type={activeToast.type} key={activeToast.id} />;
    $[17] = activeToast;
    $[18] = dequeueFirstToast;
    $[19] = isLocalStorageAvailable;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== children || $[22] !== t6) {
    t7 = <>{children}{t6}</>;
    $[21] = children;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t5 || $[25] !== t7) {
    t8 = <ToastContext.Provider value={t5}>{t7}</ToastContext.Provider>;
    $[24] = t5;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
}
function _temp2() {
  return false;
}
function _temp() {
  return typeof localStorage !== "undefined";
}